import React, { useState, useEffect, useLayoutEffect } from 'react';

// Components
import Breadcrumbs from '../../../components/Breadcrumbs';

// Utils
import { getAlert, parseCurrency, formatCurrency } from '../../../../utils/SamuiUtils';
import { 
  useDataTableKeyboardNavigation, 
  getDataTableRowStyle, 
  getDataTableKeyboardInstructions 
} from '../../../../utils/DataTableKeyboardNavigation';

const STORAGE_KEY = 'tbsetitem-list';

function loadTbSetItemFilters() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const o = JSON.parse(raw);
      const f = o.lastFilters && typeof o.lastFilters === 'object' ? o.lastFilters : {};
      return {
        searchInput: f.searchTerm ?? '',
        searchTerm: f.searchTerm ?? '',
        selectedGroup: f.groupId ?? '',
        selectedWarehouse: f.warehouseId ?? '',
        sortField: f.sortField ?? '',
        sortDirection: f.sortDirection ?? 'ASC',
      };
    }
  } catch (_) {}
  return { searchInput: '', searchTerm: '', selectedGroup: '', selectedWarehouse: '', sortField: '', sortDirection: 'ASC' };
}

function Main({ masterList, detailList, name, onPageInsert, onRowSelected, onFilterChange, groupList = [], whList = [], activeAfterSaveId, onClearActiveAfterSave, idField = 'Item_Id', listPage, listItemsPerPage, onListPageChange, onListItemsPerPageChange }) {

  const storedFilters = loadTbSetItemFilters();
  const [dataMasterList, setDataMasterList] = useState([]);
  const [internalPage, setInternalPage] = useState(1);
  const [internalItemsPerPage, setInternalItemsPerPage] = useState(50);
  const currentPage = listPage !== undefined ? listPage : internalPage;
  const itemsPerPage = listItemsPerPage !== undefined ? listItemsPerPage : internalItemsPerPage;
  const setCurrentPage = (p) => (onListPageChange ? onListPageChange(p) : setInternalPage(p));
  const setItemsPerPage = (n) => (onListItemsPerPageChange ? onListItemsPerPageChange(n) : setInternalItemsPerPage(n));
  const [searchInput, setSearchInput] = useState(storedFilters.searchInput);
  const [searchTerm, setSearchTerm] = useState(storedFilters.searchTerm);
  const [selectedGroup, setSelectedGroup] = useState(storedFilters.selectedGroup);
  const [selectedWarehouse, setSelectedWarehouse] = useState(storedFilters.selectedWarehouse);
  const [filteredItems, setFilteredItems] = useState([]);
  const [sortField, setSortField] = useState(storedFilters.sortField);
  const [sortDirection, setSortDirection] = useState(storedFilters.sortDirection);

  // DataTable keyboard navigation state
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);
  const rowRefs = React.useRef([]);
  const activeAfterSaveIdRef = React.useRef(activeAfterSaveId);

  useEffect(() => {
    activeAfterSaveIdRef.current = activeAfterSaveId;
  }, [activeAfterSaveId]);

  useEffect(() => {
    initialize();
  }, [masterList, detailList]);

  useEffect(() => {
    setFilteredItems(dataMasterList);
    // sync จาก prop ก่อนเช็ค
    activeAfterSaveIdRef.current = activeAfterSaveId;
    // ไม่ reset หน้าเมื่อ return จากการแก้ไข (activeAfterSaveId มีค่า)
    if (activeAfterSaveIdRef.current == null) setCurrentPage(1);
  }, [dataMasterList]); // ไม่ใส่ activeAfterSaveId เพื่อกัน reset ตอน onClearActiveAfterSave

  useLayoutEffect(() => {
    if (activeAfterSaveId == null || filteredItems.length === 0) return;
    const idx = filteredItems.findIndex(item => parseCurrency(item[idField]) === parseCurrency(activeAfterSaveId));
    if (idx >= 0) {
      const page = Math.floor(idx / itemsPerPage) + 1;
      const rowInPage = idx % itemsPerPage;
      setCurrentPage(page);
      setSelectedRowIndex(rowInPage);
      const timer = setTimeout(() => {
        rowRefs.current[rowInPage]?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
        onClearActiveAfterSave?.();
      }, 150);
      return () => clearTimeout(timer);
    } else {
      onClearActiveAfterSave?.();
    }
  }, [activeAfterSaveId, filteredItems, idField, itemsPerPage, onClearActiveAfterSave]);

  const initialize = async () => {
    try {
      setDataMasterList(masterList || []);
    } catch (error) {
      getAlert('FAILED', error.message);
    }
  };

  const normalizeId = (value) => {
    if (value === null || value === undefined || value === '') return '';
    return String(parseCurrency(value, 10));
  };

  const resolveGroupName = (item) => {
    if (!item) return '';
    if (item.Group_Name) return item.Group_Name;
    if (item.Item_Group_Name) return item.Item_Group_Name;
    const itemGroupId = normalizeId(item.Item_Group);
    if (!itemGroupId) return '';
    const found = groupList.find((group) => {
      const groupId = normalizeId(group.Group_Id);
      const groupCode = group.Group_Code ? String(group.Group_Code) : '';
      return (groupId && groupId === itemGroupId) || (groupCode && groupCode === String(item.Item_Group));
    });
    return found ? found.Group_Name : item.Item_Group || '';
  };

  const resolveWarehouseName = (item) => {
    if (!item) return '';
    if (item.WH_Name) return item.WH_Name;
    if (item.Item_WH_Name) return item.Item_WH_Name;
    const itemWhId = normalizeId(item.Item_WH);
    if (!itemWhId) return item.Item_WH || '';
    const found = whList.find((wh) => normalizeId(wh.WH_Id) === itemWhId);
    return found ? found.WH_Name : item.Item_WH || '';
  };

  const getGroupNameById = (groupId) => {
    if (!groupId) return "";
    const found = groupList.find((group) => normalizeId(group.Group_Id) === groupId);
    return found ? found.Group_Name : "";
  };

  const getWarehouseNameById = (whId) => {
    if (!whId) return "";
    const found = whList.find((wh) => normalizeId(wh.WH_Id) === whId);
    return found ? found.WH_Name : "";
  };

  const applyFilters = (next = {}) => {
    const nextGroupId = next.groupId !== undefined ? next.groupId : selectedGroup;
    const nextWarehouseId = next.warehouseId !== undefined ? next.warehouseId : selectedWarehouse;
    const nextSearchTerm = next.searchTerm !== undefined ? next.searchTerm : searchTerm;
    const nextSortField = next.sortField !== undefined ? next.sortField : sortField;
    const nextSortDirection = next.sortDirection !== undefined ? next.sortDirection : sortDirection;
    const groupName = getGroupNameById(nextGroupId);
    const warehouseName = getWarehouseNameById(nextWarehouseId);

    if (onFilterChange) {
      onFilterChange({
        searchTerm: nextSearchTerm,
        groupId: nextGroupId,
        groupName,
        warehouseId: nextWarehouseId,
        warehouseName,
        sortField: nextSortField,
        sortDirection: nextSortDirection,
      });
    }
  };

  const handleSort = (fieldKey) => {
    const nextDirection =
      sortField === fieldKey && sortDirection === "ASC" ? "DESC" : "ASC";
    setSortField(fieldKey);
    setSortDirection(nextDirection);
    applyFilters({ sortField: fieldKey, sortDirection: nextDirection });
    setCurrentPage(1);
    setSelectedRowIndex(0);
  };

  const renderSortHeader = (label, fieldKey, thProps = {}) => {
    const isActive = sortField === fieldKey;
    const iconClass = isActive
      ? sortDirection === "ASC"
        ? "fa fa-sort-up"
        : "fa fa-sort-down"
      : "fa fa-sort";
    return (
      <th
        {...thProps}
        className={`text-center ${thProps.className || ""}`}
        onClick={() => handleSort(fieldKey)}
        style={{ cursor: "pointer", ...thProps.style }}
        title="คลิกเพื่อเรียงลำดับ"
      >
        <span className="me-1">{label}</span>
        <i className={`${iconClass} text-muted`} />
      </th>
    );
  };

  const renderPageNumbers = () => {
    const totalPages = Math.ceil(dataMasterList.length / itemsPerPage);
    const pages = [];
    const maxVisiblePages = 5;
    const range = 2;

    if (totalPages > maxVisiblePages) {

      pages.push(
        <li
          key={1}
          className={`paginate_button page-item ${currentPage === 1 ? "active" : ""}`}
        >
          <a href="#" className="page-link" onClick={() => handlePageChange(1)}>
            1
          </a>
        </li>
      );

      if (currentPage - range > 2) {
        pages.push(
          <li key="ellipsis-start" className="paginate_button page-item">
            <a href="#" className="page-link">...</a>
          </li>
        );
      }

      for (let i = Math.max(currentPage - range, 2); i <= Math.min(currentPage + range, totalPages - 1); i++) {
        pages.push(
          <li
            key={i}
            className={`paginate_button page-item ${currentPage === i ? "active" : ""}`}
          >
            <a href="#" className="page-link" onClick={() => handlePageChange(i)}>
              {i}
            </a>
          </li>
        );
      }

      if (currentPage + range < totalPages - 1) {
        pages.push(
          <li key="ellipsis-end" className="paginate_button page-item">
            <a href="#" className="page-link">...</a>
          </li>
        );
      }

      pages.push(
        <li
          key={totalPages}
          className={`paginate_button page-item ${currentPage === totalPages ? "active" : ""}`}
        >
          <a href="#" className="page-link" onClick={() => handlePageChange(totalPages)}>
            {totalPages}
          </a>
        </li>
      );
    } else {

      for (let i = 1; i <= totalPages; i++) {
        pages.push(
          <li
            key={i}
            className={`paginate_button page-item ${currentPage === i ? "active" : ""}`}
          >
            <a href="#" className="page-link" onClick={() => handlePageChange(i)}>
              {i}
            </a>
          </li>
        );
      }
    }

    return pages;
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    setSelectedRowIndex(0);
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(parseCurrency(event.target.value));
    setCurrentPage(1);
  };


  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

  // Field mappings for keyboard navigation
  const fieldMappings = {
    no: 'Item_Id'
  };

  // Keyboard navigation
  useDataTableKeyboardNavigation(
    true, // isActive
    currentItems,
    selectedRowIndex,
    setSelectedRowIndex,
    onRowSelected,
    fieldMappings,
    currentPage,
    handlePageChange,
    filteredItems.length,
    itemsPerPage
  );

  return (
    <>
      <div className="page-header d-flex justify-content-between align-items-center mb-0">
        <Breadcrumbs page={name} items={[
          { name: 'ข้อมูลตั้งต้น', url: '/initialdata' },
          { name: name, url: '/tbsetitem' },
        ]} />
      </div>
      <div className='p-3 rounded-3 my-2 shadow-lg' style={{ backgroundColor: '#ffff' }}>
        <div className='row'>
          <div className="col-9 d-flex">
            <div className="d-flex gap-2 align-items-center w-100 flex-nowrap">
              <div className="navbar-form nav-search flex-grow-0" style={{ width: 270 }}>
                <div className="position-relative">
                  <input
                    type="text"
                    placeholder="พิมพ์แล้วกด Enter เพื่อค้นหา..."
                    className="form-control"
                    style={{ paddingRight: 36, border: "1px solid #ced4da" }}
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        setSearchTerm(searchInput);
                        applyFilters({ searchTerm: searchInput });
                        setCurrentPage(1);
                        setSelectedRowIndex(0);
                      }
                    }}
                  />
                  {searchTerm ? (
                    <button
                      type="button"
                      className="btn p-0 position-absolute top-50 translate-middle-y"
                      style={{ right: 8, color: "#6c757d" }}
                      onClick={() => {
                        setSearchInput("");
                        setSearchTerm("");
                        applyFilters({ searchTerm: "" });
                        setCurrentPage(1);
                        setSelectedRowIndex(0);
                      }}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  ) : (
                    <span
                      className="position-absolute top-50 translate-middle-y"
                      style={{ right: 10, color: "#6c757d" }}
                    >
                      <i className="fas fa-search"></i>
                    </span>
                  )}
                </div>
              </div>
              <div className="d-flex align-items-center" style={{ marginLeft: 8 }}>
                <span className="me-1">กลุ่มสินค้า</span>
                <div className="input-group" style={{ width: 180 }}>
                <select
                  className="form-select"
                  style={{ height: 38 }}
                  value={selectedGroup}
                  onChange={(e) => {
                    setSelectedGroup(e.target.value);
                    applyFilters({ groupId: e.target.value });
                    setCurrentPage(1);
                    setSelectedRowIndex(0);
                  }}
                  disabled={groupList.length <= 0}
                >
                  <option value="">ทั้งหมด</option>
                  {groupList.map((group) => (
                    <option key={group.Group_Id} value={normalizeId(group.Group_Id)}>
                      {group.Group_Name}
                    </option>
                  ))}
                </select>
                </div>
              </div>
              <div className="d-flex align-items-center" style={{ marginLeft: 8 }}>
                <span className="me-1">คลังสินค้า</span>
                <div className="input-group" style={{ width: 180 }}>
                <select
                  className="form-select"
                  style={{ height: 38 }}
                  value={selectedWarehouse}
                  onChange={(e) => {
                    setSelectedWarehouse(e.target.value);
                    applyFilters({ warehouseId: e.target.value });
                    setCurrentPage(1);
                    setSelectedRowIndex(0);
                  }}
                  disabled={whList.length <= 0}
                >
                  <option value="">ทั้งหมด</option>
                  {whList.map((wh) => (
                    <option key={wh.WH_Id} value={normalizeId(wh.WH_Id)}>
                      {wh.WH_Name}
                    </option>
                  ))}
                </select>
                </div>
              </div>
            </div>
          </div>
          <div className="col-3 d-flex justify-content-end">
            <button
              onClick={onPageInsert}
              className="btn btn-warning text-white ms-2"
              type="button"
            >
              สร้าง{name} <i className="fa fa-plus"></i>
            </button>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <div className="card p-2">
            <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto' }}>
              <table id="basic-datatables" className="table table-striped table-hover text-nowrap">
                <thead className="thead-dark">
                  <tr>
                    <th className="text-center" style={{ width: "2%" }}>
                      #
                    </th>
                    {/* <th className="text-center" style={{ width: "9%" }} >
                      Barcode
                    </th> */}
                    {renderSortHeader("รหัสสินค้า", "item_code", { style: { width: "11%" } })}
                    {renderSortHeader("ชื่อสินค้า", "item_name", { style: { width: "23%" } })}
                    {renderSortHeader("ชื่อสินค้าแบบย่อ", "item_short_name")}
                    {renderSortHeader("กลุ่มสินค้า", "item_group")}
                    {renderSortHeader("คลังสินค้า", "item_wh")}
                    <th className="text-center" style={{ width: "7%" }}>
                      หน่วยสินค้ารับเข้า
                    </th>
                    <th className="text-center" style={{ width: "7%" }}>
                      หน่วยสินค้าในคลัง
                    </th>
                    <th className="text-center" style={{ width: "7%" }}>
                      หน่วยสินค้าส่งออก
                    </th>
                    {renderSortHeader("ต้นทุนสินค้า", "item_cost", { style: { width: "7%" } })}
                    {renderSortHeader("ราคาสินค้า", "item_price", { style: { width: "7%" } })}
                    {renderSortHeader("ราคาสินค้าA", "item_pricea", { style: { width: "7%" } })}
                    {renderSortHeader("ราคาสินค้าB", "item_priceb", { style: { width: "7%" } })}
                    <th className="text-center" style={{ width: "3%" }}>
                      แก้ไข
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((item, index) => (
                      <tr
                        key={index}
                        ref={el => rowRefs.current[index] = el}
                        style={{ cursor: "pointer", ...getDataTableRowStyle(index, selectedRowIndex) }}
                      >
                        <td className="text-center">{indexOfFirstItem + index + 1}</td>
                        <td className="text-center">
                          {item.Item_Code}
                        </td>
                        <td className="text-left">
                          {item.Item_Name}
                        </td>
                        <td className="text-center">
                          {item.Item_Short_Name}
                        </td>
                        <td className="text-center">
                          {resolveGroupName(item)}
                        </td>
                        <td className="text-center">
                          {resolveWarehouseName(item)}
                        </td>
                        <td className="text-center">
                          {item.Item_Unit_IN}
                        </td>
                        <td className="text-center">
                          {item.Item_Unit_ST}
                        </td>
                        <td className="text-center">
                          {item.Item_Unit_OUT}
                        </td>
                        <td className="text-end">
                          {formatCurrency(parseCurrency(item.Item_Cost))}
                        </td>
                        <td className="text-end">
                          {formatCurrency(parseCurrency(item.Item_Price))}
                        </td>
                        <td className="text-end">
                          {formatCurrency(parseCurrency(item.Item_PriceA))}
                        </td>
                        <td className="text-end">
                          {formatCurrency(parseCurrency(item.Item_PriceB))}
                        </td>
                        <td className="text-center">
                          <i onClick={() => onRowSelected(item)} className="fas fa-edit fw-bold" style={{ color: "#EF6C00" }} />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="14">
                        <center>
                          <h5>ไม่พบข้อมูล</h5>
                        </center>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Keyboard instructions */}
            <div className="mt-2">
              <small className="text-muted">
                {getDataTableKeyboardInstructions()}
              </small>
            </div>
            
            <div className="row mt-3">
              <div className="col-12 d-flex justify-content-between">
                {/* Dropdown for Items Per Page */}
                <div className="d-flex align-items-center">
                  <label htmlFor="itemsPerPage" className="mr-2">Items per page:</label>
                  <select
                    id="itemsPerPage"
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                    className="form-select"
                  >
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={150}>150</option>
                    <option value={dataMasterList.length}>All</option>
                  </select>
                </div>

                <div className="dataTables_paginate paging_simple_numbers">
                  <ul className="pagination">
                    <li className={`paginate_button page-item previous ${currentPage === 1 ? "disabled" : ""}`}>
                      <a
                        href="#"
                        className="page-link"
                        onClick={() => handlePageChange(currentPage - 1)}
                      >
                        Previous
                      </a>
                    </li>
                    {renderPageNumbers()}
                    <li
                      className={`paginate_button page-item next ${currentPage ===
                        Math.ceil(dataMasterList.length / itemsPerPage)
                        ? "disabled"
                        : ""
                        }`}
                    >
                      <a
                        href="#"
                        className="page-link"
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        Next
                      </a>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div >
    </>
  );
}

export default Main;